import { ChangeEvent, useCallback, useMemo } from 'react';

import { useWatch } from '../watchUpdate';
import { MultiStateHook, useMultiState } from '../multiState';
import { AppSelectChangeEvent } from 'components/Blocks';

/*
 **
 ** ---------> Memorization is VITAL for these <---------------------
 **
 */

type FormControlChangeEvent =
  | AppSelectChangeEvent<boolean>
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

export type FormControlHook<DefaultValues> = Omit<MultiStateHook<DefaultValues>, 'values'> & {
  handleChange: (event: FormControlChangeEvent) => void;

  // temp as we refactor around
  state: MultiStateHook<DefaultValues>['values'];
};

export type FormControlDispatchAction<DefaultValues> = MultiStateHook<DefaultValues>['dispatch'];

interface ControlOptions {
  watch?: boolean;
}

export function useFormControl<FormValues extends object>(
  defaultValues: FormValues,
  { watch = false }: ControlOptions = {},
): FormControlHook<FormValues> {
  const { values, dispatch, resetDefault, peek, remove, set, getSetter } =
    useMultiState(defaultValues);

  useWatch(defaultValues, () => watch && dispatch(defaultValues));

  const handleChange = useCallback(
    ({ target }: FormControlChangeEvent) => {
      const { id, name, value } = target;

      dispatch({
        [(id || name) ?? '__BAD_UPDATE__']: value,
      } as Partial<FormValues>);
    },
    // we know dispatch never changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const control = useMemo(
    () => ({
      state: values,
      dispatch,
      handleChange,
      resetDefault,
      peek,
      remove,
      set,
      getSetter,
    }),
    // we know our handlers from multiState do not change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values],
  );

  return control as FormControlHook<FormValues>;
}
